import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import AllBreedCandidateDialog, { SOURCE_LABEL } from '../components/AllBreedCandidateDialog';
import { allbreedAPI } from '../services/api';
import { toArray } from '../utils/apiData';
import { formatUtcDate } from '../utils/dateFormat';
import { cardSx } from '../theme';

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_CHIP = {
  pending:      { label: 'Pending',      color: 'default' },
  needs_review: { label: 'Needs Review', color: 'warning' },
  ready:        { label: 'Ready',        color: 'success' },
  error:        { label: 'Error',        color: 'error' },
  skipped:      { label: 'Skipped',      color: 'default' },
  submitted:    { label: 'Submitted',    color: 'success' },
};

function StatusChip({ status }) {
  const cfg = STATUS_CHIP[status] || { label: status, color: 'default' };
  return <Chip size="small" label={cfg.label} color={cfg.color} />;
}

// ---------------------------------------------------------------------------
// Parent resolution cell
// ---------------------------------------------------------------------------

function ParentCell({ label, parent, resolvedSlug, resolvedSource, onPickCandidate }) {
  if (!parent) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">{label}: —</Typography>
      </Box>
    );
  }

  if (resolvedSlug) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CheckCircleIcon fontSize="small" color="success" />
          <Typography variant="body2">{parent.name}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {resolvedSlug} · {SOURCE_LABEL[resolvedSource] || resolvedSource}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <HelpOutlineIcon fontSize="small" color="action" />
        <Typography variant="body2">{parent.name}</Typography>
      </Stack>
      {onPickCandidate && (
        <Button size="small" sx={{ mt: 0.5, p: 0 }} onClick={onPickCandidate}>
          Pick from candidates
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Queue row
// ---------------------------------------------------------------------------

function QueueRow({ horse, onRefresh }) {
  const [resolving, setResolving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState(null);

  // Pending resolve result waiting for Kyle to pick from multiples
  const [sireResult, setSireResult] = useState(null);
  const [damResult, setDamResult] = useState(null);
  const [pickTarget, setPickTarget] = useState(null); // 'sire' | 'dam'

  const sub = horse.submission;
  const sireSlug = sub?.sire_allbreed_slug;
  const damSlug = sub?.dam_allbreed_slug;
  const sireSource = sub?.sire_resolved_source;
  const damSource = sub?.dam_resolved_source;

  const canApprove = horse.sire && horse.dam && horse.allbreed_status !== 'ready';

  async function handleResolve() {
    setResolving(true);
    setError(null);
    setSireResult(null);
    setDamResult(null);
    try {
      const { data } = await allbreedAPI.resolve(horse.id);
      console.log('[resolve]', JSON.stringify(data));

      const sire = data.sire;
      const dam = data.dam;

      // Auto-select if single result; otherwise store for user to pick.
      const updates = {};
      let hasPendingMultiple = false;
      if (sire) {
        if (sire.status === 'multiple') {
          setSireResult(sire);
          hasPendingMultiple = true;
        } else if (sire.candidates?.length) {
          updates.sire_slug = sire.candidates[0].slug;
          updates.sire_source = sire.candidates[0].source;
        }
      }
      if (dam) {
        if (dam.status === 'multiple') {
          setDamResult(dam);
          hasPendingMultiple = true;
        } else if (dam.candidates?.length) {
          updates.dam_slug = dam.candidates[0].slug;
          updates.dam_source = dam.candidates[0].source;
        }
      }

      if (updates.sire_slug || updates.dam_slug) {
        await allbreedAPI.selectParents(horse.id, updates);
        if (!hasPendingMultiple) onRefresh();
      } else {
        const sireStatus = sire?.status;
        const damStatus = dam?.status;
        if (sireStatus === 'error' || damStatus === 'error') {
          setError('AllBreed network lookup blocked — Playwright will search by name at submit time.');
        } else if (sireStatus === 'none' || damStatus === 'none') {
          setError('Parent not found on AllBreed — verify the name or add the parent manually first.');
        } else if (sireStatus === 'multiple' || damStatus === 'multiple') {
          // dialogs are open, nothing to do
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setResolving(false);
    }
  }

  async function handlePickCandidate(role, candidate) {
    setPickTarget(null);
    try {
      const updates =
        role === 'sire'
          ? { sire_slug: candidate.slug, sire_source: candidate.source }
          : { dam_slug: candidate.slug, dam_source: candidate.source };
      await allbreedAPI.selectParents(horse.id, updates);
      // If the other parent still needs picking, clear only this result and
      // don't refresh (which would unmount and lose the other pending result).
      const otherStillPending = role === 'sire' ? damResult : sireResult;
      if (otherStillPending) {
        if (role === 'sire') setSireResult(null);
        else setDamResult(null);
      } else {
        onRefresh();
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  async function handleApprove() {
    setApproving(true);
    setError(null);
    try {
      await allbreedAPI.approve(horse.id);
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setApproving(false);
    }
  }

  async function handleSkip() {
    setSkipping(true);
    setError(null);
    try {
      await allbreedAPI.skip(horse.id);
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSkipping(false);
    }
  }

  const activeCandidates =
    pickTarget === 'sire'
      ? sireResult?.candidates || []
      : pickTarget === 'dam'
      ? damResult?.candidates || []
      : [];

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>{horse.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[horse.sex, horse.date_of_birth?.slice(0, 4)].filter(Boolean).join(' · ')}
          </Typography>
        </TableCell>
        <TableCell>
          <ParentCell
            label="Sire"
            parent={horse.sire}
            resolvedSlug={sireSlug}
            resolvedSource={sireSource}
            onPickCandidate={sireResult ? () => setPickTarget('sire') : null}
          />
        </TableCell>
        <TableCell>
          <ParentCell
            label="Dam"
            parent={horse.dam}
            resolvedSlug={damSlug}
            resolvedSource={damSource}
            onPickCandidate={damResult ? () => setPickTarget('dam') : null}
          />
        </TableCell>
        <TableCell>
          <StatusChip status={horse.allbreed_status} />
          {horse.allbreed_last_error && (
            <Tooltip title={horse.allbreed_last_error}>
              <ErrorIcon fontSize="small" color="error" sx={{ ml: 0.5 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              onClick={handleResolve}
              disabled={resolving || approving}
            >
              {resolving ? <CircularProgress size={16} /> : 'Resolve'}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApprove}
              disabled={!canApprove || approving || resolving}
            >
              {approving ? <CircularProgress size={16} /> : 'Approve'}
            </Button>
            <Tooltip title="Skip — won't show in queue again">
              <span>
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleSkip}
                  disabled={skipping}
                  sx={{ minWidth: 0, px: 1 }}
                >
                  <SkipNextIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {error && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {error}
            </Typography>
          )}
        </TableCell>
      </TableRow>

      <AllBreedCandidateDialog
        open={pickTarget !== null}
        title={`Multiple matches — pick the ${pickTarget}`}
        candidates={activeCandidates}
        onSelect={(c) => handlePickCandidate(pickTarget, c)}
        onClose={() => setPickTarget(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Ready tab
// ---------------------------------------------------------------------------

function ReadyRow({ horse, onRefresh }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const sub = horse.submission;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      await allbreedAPI.submit(horse.id);
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>{horse.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {[horse.sex, horse.date_of_birth?.slice(0, 4)].filter(Boolean).join(' · ')}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{horse.sire?.name || '—'}</Typography>
        {sub?.sire_allbreed_slug && (
          <Typography variant="caption" color="text.secondary">{sub.sire_allbreed_slug}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2">{horse.dam?.name || '—'}</Typography>
        {sub?.dam_allbreed_slug && (
          <Typography variant="caption" color="text.secondary">{sub.dam_allbreed_slug}</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={16} /> : 'Submit to AllBreed'}
        </Button>
        {error && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

function HistoryTable({ rows }) {
  if (!rows.length) {
    return <Typography color="text.secondary" sx={{ p: 2 }}>No submission history yet.</Typography>;
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Horse</TableCell>
            <TableCell>AllBreed ID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>{row.horse_name || `Horse #${row.horse_id}`}</TableCell>
              <TableCell>
                {row.response_allbreed_id || row.allbreed_horse_id || '—'}
              </TableCell>
              <TableCell><StatusChip status={row.status} /></TableCell>
              <TableCell>
                {formatUtcDate(row.attempted_at) || formatUtcDate(row.created_at) || '—'}
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="error">
                  {row.error_message || ''}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AllBreedSubmissionsPage() {
  const [tab, setTab] = useState(0);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [qRes, hRes] = await Promise.all([
        allbreedAPI.getQueue(),
        allbreedAPI.getHistory(),
      ]);
      setQueue(toArray(qRes.data, 'allbreed queue'));
      setHistory(toArray(hRes.data, 'allbreed history'));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const pendingHorses = queue.filter(
    (h) => !['ready'].includes(h.allbreed_status),
  );
  const readyHorses = queue.filter((h) => h.allbreed_status === 'ready');

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 6 }}>
      <Typography variant="h5" gutterBottom>AllBreed Submissions</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={cardSx}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Queue (${pendingHorses.length})`} />
          <Tab label={`Ready to Submit (${readyHorses.length})`} />
          <Tab label={`History (${history.length})`} />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tab === 0 && (
              pendingHorses.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 3 }}>
                  No horses pending — all caught up!
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Horse</TableCell>
                        <TableCell>Sire</TableCell>
                        <TableCell>Dam</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingHorses.map((h) => (
                        <QueueRow key={h.id} horse={h} onRefresh={loadData} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}

            {tab === 1 && (
              readyHorses.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 3 }}>
                  No horses approved yet — resolve and approve parents in the Queue tab.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Horse</TableCell>
                        <TableCell>Sire (AllBreed slug)</TableCell>
                        <TableCell>Dam (AllBreed slug)</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {readyHorses.map((h) => (
                        <ReadyRow key={h.id} horse={h} onRefresh={loadData} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}

            {tab === 2 && <HistoryTable rows={history} />}
          </>
        )}
      </Paper>
    </Container>
  );
}
